import {
  Input,
  Component,
  isDevMode,
  OnChanges,
  ChangeDetectionStrategy,
} from '@angular/core';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { NgpButton } from 'ng-primitives/button';

export type SuiButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'danger'
  | 'success'
  | 'warning'
  | 'ghost'
  | 'legacy-ghost';

export type SuiButtonSize = 'xsm' | 'sm' | 'md' | 'lg';
export type SuiButtonType = 'button' | 'submit' | 'reset';
/** `round` renders a fully rounded button - meant for icon-only buttons (dialog close, macOS-style). */
export type SuiButtonShape = 'default' | 'round';

@Component({
  selector: 'sui-button',
  standalone: true,
  imports: [NgpButton, FaIconComponent],
  templateUrl: './sui-button.component.html',
  styleUrl: './sui-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SuiButtonComponent implements OnChanges {
  @Input({ required: true }) variant!: SuiButtonVariant;
  @Input() size: SuiButtonSize = 'md';
  @Input() type: SuiButtonType = 'button';
  @Input() disabled = false;
  @Input() loading = false;
  @Input() iconOnly = false;
  @Input() shape: SuiButtonShape = 'default';
  @Input() leadingIcon?: IconProp;
  /**
   * Render the icons at Font Awesome's fixed 1.25em width so a row of icon-only
   * buttons with glyphs of different widths (caret vs step) stays uniform.
   * Opt-in: the default keeps each glyph's natural width.
   */
  @Input() iconFixedWidth = false;
  /**
   * Font Awesome transform string forwarded to both icons (e.g. `grow-6`), for
   * glyphs whose drawing is small inside its em box (carets) and must match
   * neighbouring icons visually. Scales the drawing only; the button box is
   * unchanged. Opt-in.
   */
  @Input() iconTransform?: string;
  @Input() trailingIcon?: IconProp;
  /**
   * Label text the button owns: hidden while `iconOnly` and used as the
   * `aria-label` fallback then. Pass `label` OR project text as content, never both.
   */
  @Input() label?: string;

  /** Forwarded to the inner button — required for icon-only buttons without `label`. */
  @Input() ariaLabel?: string;
  /** Sticky active state (replaces legacy `selected` / `toolbar-selected`). */
  @Input() pressed?: boolean;
  /** Open-dropdown state — rotates the trailing icon and tints the button. */
  @Input() expanded?: boolean;

  get buttonClasses(): string {
    const classes = [
      'sui-button',
      `sui-button--${this.variant}`,
      `sui-button--${this.size}`,
    ];
    if (this.iconOnly) {
      classes.push('sui-button--icon-only');
    }
    if (this.shape === 'round') {
      classes.push('sui-button--round');
    }
    if (this.loading) {
      classes.push('sui-button--loading');
    }
    return classes.join(' ');
  }

  ngOnChanges(): void {
    if (isDevMode() && this.iconOnly && !this.ariaLabel && !this.label) {
      console.warn(
        '[sui-button] icon-only button is missing an `ariaLabel` - it is invisible to screen readers.',
      );
    }
  }

  onClick(event: Event): void {
    // Loading is visually active but non-interactive: swallow the click so it
    // doesn't reach the consumer's (click) handler. (disabled is blocked natively.)
    if (this.loading) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }
}
